import React, { useState, useEffect, useRef } from 'react';
import {
  isPdfFile,
  isWordFile,
  getDocumentPreviewUrl,
  getDocumentUrl
} from '../utils/fileUtils';
import { enhancedHighlightHtml } from '../utils/enhancedHighlighter';
import { getApiUrl } from '../utils/api';

interface DocumentViewerProps {
  filePath: string;
  fileName?: string;
  documentQuotes?: string[];
}

const DocumentViewer: React.FC<DocumentViewerProps> = ({ filePath, fileName, documentQuotes }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [htmlContent, setHtmlContent] = useState<string | null>(null);
  const iframeRef = useRef<HTMLIFrameElement>(null);

  // Extract the actual file name from the path
  const extractedFileName = filePath.split('/').pop() || '';

  useEffect(() => {
    // Reset loading state when file changes
    setLoading(true);
    setError(false);
    setHtmlContent(null);

    // Simulate short load time to show loading indicator
    const timer = setTimeout(() => {
      setLoading(false);
    }, 1500);

    return () => clearTimeout(timer);
  }, [filePath]);

  // Check if the most recent message starts with "✅ Yes"
  const checkIfPositiveResponse = () => {
    // Find the most recent message in the parent component
    // This is a simplified approach - you may need to get this data from props
    const messages = document.querySelectorAll('.markdown');
    if (messages && messages.length > 0) {
      // Get the last message content
      const lastMessage = messages[messages.length - 1]?.textContent || '';
      return lastMessage.trim().startsWith('✅ Yes');
    }
    return false;
  };

  // Effect for handling highlighting when document quotes change
  useEffect(() => {
    // Only process for Word documents
    if (!isWordFile(extractedFileName)) {
      return;
    }

    // Check if we should skip highlighting due to a positive response
    const isPositiveResponse = checkIfPositiveResponse();

    // Always process even without quotes to show the document
    // and keep debug information visible
    const hasQuotes = documentQuotes && documentQuotes.length > 0 && !isPositiveResponse;

    // Fetch the HTML content to apply highlighting (even without quotes)
    const fetchAndHighlightHtml = async () => {
      try {
        // We'll always fetch the content regardless of quotes
        const basePreviewUrl = getApiUrl(`documents/${extractedFileName}/html`);
        const response = await fetch(basePreviewUrl);
        const html = await response.text();

        // Log document content info for debugging
        console.log(`Fetched HTML document: ${extractedFileName}`);
        console.log(`Document length: ${html.length} characters`);
        console.log(`Quote status: ${hasQuotes ? documentQuotes!.length + ' quotes available' : 'No quotes available'}`);

        if (isPositiveResponse) {
          console.log('Skipping highlights due to positive "✅ Yes" response');
          setHtmlContent(html); // Use plain HTML without highlights
          return;
        }

        // Apply highlighting if quotes exist, otherwise just use the plain HTML
        if (hasQuotes) {
          try {
            const highlightedHtml = enhancedHighlightHtml(html, documentQuotes!);
            setHtmlContent(highlightedHtml);
            console.log(`Applied enhanced highlighting for ${documentQuotes!.length} quotes`);

            // Dump quotes to console for debugging
            console.log('Quotes for highlighting:');
            documentQuotes!.forEach((quote, i) => {
              console.log(`Quote ${i+1}: ${quote.substring(0, 100)}${quote.length > 100 ? '...' : ''}`);
            });
          } catch (highlightError) {
            console.error('Error during highlighting:', highlightError);
            // If highlighting fails, still show the document without highlights
            setHtmlContent(html);
          }
        } else {
          setHtmlContent(html);
          console.log('No quotes available for highlighting');
        }
      } catch (error) {
        console.error('Error fetching and highlighting HTML:', error);
        setError(true);
      }
    };

    fetchAndHighlightHtml();
  }, [documentQuotes, extractedFileName]);

  // Handle PDF highlighting (when iframe is loaded)
  useEffect(() => {
    // Only process for PDF documents with an iframe
    if (!isPdfFile(extractedFileName) || !iframeRef.current) {
      return;
    }

    // Check if we have quotes to highlight
    const hasQuotes = documentQuotes && documentQuotes.length > 0;

    // For PDF files, we'll implement PDF.js-based highlighting in the future
    // This is a placeholder that runs regardless of whether there are quotes
    const iframe = iframeRef.current;

    const handleIframeLoad = () => {
      try {
        if (hasQuotes) {
          // PDF highlighting could be implemented here using PDF.js
          console.log(`PDF loaded - would highlight ${documentQuotes!.length} sections`);
        } else {
          console.log('PDF loaded - no quotes to highlight');
        }
      } catch (error) {
        console.error('Error with PDF:', error);
      }
    };

    iframe.addEventListener('load', handleIframeLoad);
    return () => {
      iframe.removeEventListener('load', handleIframeLoad);
    };
  }, [documentQuotes, extractedFileName]);

  const handleLoad = () => {
    setLoading(false);
  };

  const handleError = () => {
    setLoading(false);
    setError(true);
  };

  const previewUrl = getDocumentPreviewUrl(
    getApiUrl('documents'),
    extractedFileName,
    documentQuotes
  );
  const downloadUrl = getDocumentUrl(getApiUrl('documents'), extractedFileName);
  
  return (
    <div className="h-full relative">
      {/* Loading indicator */}
      {loading && (
        <div className="absolute inset-0 flex items-center justify-center bg-white bg-opacity-80 z-10">
          <div className="flex flex-col items-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-700 mb-3"></div>
            <p className="text-gray-700">Loading document...</p>
          </div>
        </div>
      )}
      
      {/* Error state */}
      {error && (
        <div className="flex items-center justify-center h-full">
          <div className="p-6 text-center">
            <p className="text-gray-500">Unable to display this document</p>
            <p className="text-sm text-gray-400 mt-2">{fileName || extractedFileName}</p>
            <a
              href={downloadUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-block mt-4 bg-gray-200 hover:bg-gray-300 px-3 py-1.5 rounded-md text-sm"
            >
              Download File
            </a>
          </div>
        </div>
      )}
      
      {/* Document display */}
      {!error && (
        <>
          {/* Use highlighted HTML content if available (for Word documents) */}
          {htmlContent && isWordFile(extractedFileName) ? (
            <iframe
              ref={iframeRef}
              srcDoc={htmlContent}
              className="w-full h-full border-0"
              onLoad={handleLoad}
              onError={handleError}
              title="Document Preview with Highlights"
            />
          ) : (
            /* Regular preview for documents without highlighting */
            <iframe
              ref={iframeRef}
              src={previewUrl}
              className="w-full h-full border-0"
              onLoad={handleLoad}
              onError={handleError}
              title="Document Preview"
            />
          )}

          {/* Highlight info indicator - always show for debugging */}
          <div className={`absolute bottom-4 right-4 px-3 py-2 rounded-md shadow-sm text-sm font-medium ${
            documentQuotes && documentQuotes.length > 0
              ? "bg-yellow-100 text-yellow-800"
              : "bg-red-100 text-red-800"
          }`}>
            {documentQuotes && documentQuotes.length > 0 ? (
              <span className="flex items-center">
                <svg className="w-4 h-4 mr-1 text-yellow-600" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2h-1V9z" clipRule="evenodd"></path>
                </svg>
                {documentQuotes.length} highlighted {documentQuotes.length === 1 ? 'section' : 'sections'}
              </span>
            ) : (
              <span className="flex items-center">
                <svg className="w-4 h-4 mr-1 text-red-600" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2h-1V9z" clipRule="evenodd"></path>
                </svg>
                No sections highlighted
              </span>
            )}
          </div>

          {/* Debug button to toggle quote details */}
          <div className="absolute bottom-4 left-4">
            <button
              onClick={() => {
                if (documentQuotes && documentQuotes.length > 0) {
                  alert(`Document contains ${documentQuotes.length} quotes:\n\n${documentQuotes.map((q, i) =>
                    `Quote ${i+1}: "${q.substring(0, 100)}${q.length > 100 ? '...' : ''}"`
                  ).join('\n\n')}`);
                } else {
                  alert('No quotes available for this document.');
                }
              }}
              className="px-3 py-2 rounded-md shadow-sm text-sm font-medium bg-gray-100 text-gray-700 hover:bg-gray-200"
            >
              <span className="flex items-center">
                <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4zm2 6a1 1 0 011-1h6a1 1 0 110 2H7a1 1 0 01-1-1zm1 3a1 1 0 100 2h6a1 1 0 100-2H7z" clipRule="evenodd"></path>
                </svg>
                Debug Quotes
              </span>
            </button>
          </div>
        </>
      )}
    </div>
  );
};

export default DocumentViewer;